import { DeviceEventEmitter, Platform } from "react-native";
import React, { useContext } from "react";
import { Button } from "@components/atoms";
import styled, { useTheme } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useDispatch, useSelector } from "react-redux";
import {
  clearSubCollections,
  resetFiltersPayloadByProduct,
  resetJewelryFiltersPayload,
  setCollection,
  setFiltersPayload,
  setFiltersSet,
  setPayloadData,
  setSectionName,
  setSelectedCollectionOption,
  useSearchFiltersAuxPayload,
  useSearchFiltersSlice,
} from "@redux/slices/searchFilters";
import Routes from "@navigations/Routes";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import {
  ProductCategory,
  ProductTypeCommon,
  SectionName,
} from "@utils/constants";
import { getMinMaxForShape, handleFilters, normalizeBoolean } from "./Utils";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { VDBIcons } from "@utils/VDBIcons";
import {
  productNameToTokenCategory,
  removeSecureStoreItems,
} from "@utils/index";
import { useTranslation } from "react-i18next";
import { FilterScreenContext } from "@scenes/explore/FilterScreenContext";
import { useProductPrefix } from "../../../../hooks/useProductPrefix";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useNavigationHistory } from "../../../../hooks/useNavigationHistory";
import { setBuilderProduct } from "@redux/slices/appStateSlice";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { css } from "styled-components/native";
import { SearchFilterTypeEnum } from "../utils";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { goBack, navigate, push } from "@navigations/utils/RootNavigation";
import { NavigationHistoryState } from "@components/molecules/Breadcrumbs/utils";
import { useEffect, useRef } from "react";
import { DefaultTheme } from "styled-components/native";
import { useScrollbarWidth } from "@components/atoms/SectionContent/utils";
import { useAppSettings } from "@redux/slices/globalSlice";

type ButtonSectionType = {
  isPDPLoaded: boolean;
  filterContainerWidth?: number;
  showSearchWithArrow?: boolean;
  showAsTopButtons?: boolean;
  hideResetFilter?: boolean;
  skipFilterScreenForWeb?: boolean;
  containerBackgroundColor?: string;
  enterSecondFlow?: boolean;
  isOnSplitScreen?: boolean;
  fromResultScreen: boolean;
  filters?: any;
  showDiamondClassicAiTabs?: boolean;
};

const ButtonSection = ({
  isPDPLoaded,
  filterContainerWidth,
  showSearchWithArrow,
  showAsTopButtons,
  skipFilterScreenForWeb,
  fromResultScreen,
  containerBackgroundColor,
  filters,
  enterSecondFlow,
  isOnSplitScreen = false,
  showDiamondClassicAiTabs,
}: ButtonSectionType) => {
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  // const [showCaratModal, setShowCaratModal] = useState(false);
  const { history } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );
  const { jcSelectedOption } = useJewelryConfiguratorState();
  const { bottom } = useSafeAreaInsets();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { addRouteToHistory, clearRouteHistory } = useNavigationHistory();
  let auxPayload = useSearchFiltersAuxPayload();
  const { sectionName } = useSearchFiltersSlice();
  const { productType } = useContext(FilterScreenContext);
  const productTheme = productNameToTokenCategory(
    productType as ProductCategory
  );
  const { t } = useTranslation();
  const productPrefix = useProductPrefix();
  const {
    collection: collections,
    min_max_Options,
    auxPayload: filtersPayload,
    selectedCollectionOption,
  } = useSearchFiltersSlice();
  const setPreservedData = !!(collections && collections.length > 0);
  const isTablet = theme.isTablet || theme.isLargeScreen;
  const priceMode = useSelector(
    (state: any) => state?.userInfo?.user?.price_mode
  );
  const { isIFrame } = useUserInfo();
  const getAppSettings = useAppSettings();
  const iframeFlowV2 = getAppSettings?.iframe_flow_v2 === true;
  const isIFrameFlow = () => isIFrame() && iframeFlowV2;
  const userInfo = useUserInfo();
  const normalizedEnterSecondFlow = normalizeBoolean(enterSecondFlow);
  const { deviceWidth: screenWidth } = useIsFoldable();
  const modifyShapeSupportForBuilder =
    min_max_Options?.supported_shapes?.length > 0 &&
    userInfo.user.feature_settings?.allow_search_modify_in_builder &&
    enableJewelryBuilder &&
    normalizedEnterSecondFlow;
  const shapeFilter = Array.isArray(filtersPayload[sectionName]?.shapes)
    ? filtersPayload[sectionName]?.shapes[0]
    : filtersPayload[sectionName]?.shapes;
  const { minOption, maxOption } = getMinMaxForShape(
    modifyShapeSupportForBuilder as boolean,
    min_max_Options,
    shapeFilter
  ) || {
    min: null,
    max: null,
  };
  let buttonWidth =
    ((isTablet ? filterContainerWidth ?? 0 : screenWidth ?? 0) -
      3 * SpaceType.Double) /
    (2 - (theme.isLargeScreen && fromResultScreen ? 1 : 0));

  if (enableJewelryBuilder) {
    if (normalizedEnterSecondFlow === true) {
      buttonWidth = isTablet
        ? filterContainerWidth ?? 0
        : screenWidth - 2 * SpaceType.Double;
    }
  } else if (skipFilterScreenForWeb) {
    buttonWidth = buttonWidth / 2;
  }

  const parcelPayloadName = filters?.find(
    (item: any) => item.name === SearchFilterTypeEnum.PARCEL
  )?.payload_name?.[0];

  const iframePayloadRef = useRef<any>(null);

  useEffect(() => {
    const listener = DeviceEventEmitter.addListener(
      "IFRAME_FILTER_PAYLOAD",
      (payload) => {
        iframePayloadRef.current = payload;
      }
    );

    return () => listener.remove();
  }, []);

  const onApplyFilters = () => {
    if (enableJewelryBuilder && normalizedEnterSecondFlow === true) {
      const supportedCaratFrom = modifyShapeSupportForBuilder
        ? minOption
        : jcSelectedOption?.current?.option_value?.supported_carat_from;
      const supportedCaratTo = modifyShapeSupportForBuilder
        ? maxOption
        : jcSelectedOption?.current?.option_value?.supported_carat_to;
      const payloadSelectFrom = auxPayload[sectionName].size_from;
      const payloadSelectTo = auxPayload[sectionName].size_to;

      if (
        Number(payloadSelectFrom) < Number(supportedCaratFrom) ||
        Number(payloadSelectFrom) > Number(supportedCaratTo) ||
        Number(payloadSelectTo) > Number(supportedCaratTo) ||
        Number(payloadSelectTo) < Number(supportedCaratFrom)
      ) {
        // setShowCaratModal(true);
        DeviceEventEmitter.emit("caratRangeValid", {
          from: payloadSelectFrom,
          to: payloadSelectTo,
        });
        return;
      }
    }

    if (theme.isMobile) {
      DeviceEventEmitter.emit("CLEAR_RESULTS_EVENT");
    }

    // auxPayload = Object.fromEntries(
    //   Object.entries(auxPayload).map(([key, value]) => {
    //     if (
    //       value &&
    //       typeof value === "object" &&
    //       "cert_num" in value &&
    //       !isIFrame()
    //     ) {
    //       // Create a copy of the object and delete cert_num
    //       const updatedValue = { ...value };
    //       delete updatedValue.cert_num;
    //       return [key, updatedValue];
    //     }
    //     return [key, value];
    //   })
    // );

    let payload = { ...auxPayload };
    if (sectionName?.toLowerCase() === "pairs") {
      payload[sectionName] = {
        ...payload[sectionName],
        pair: "pair",
      };
      dispatch(setFiltersPayload({ sectionName, data: payload[sectionName] }));
    } else if (sectionName?.toLowerCase() === "parcel") {
      payload[sectionName] = {
        ...payload[sectionName],
        ...(parcelPayloadName?.pair && { pair: parcelPayloadName?.pair }),
      };
      dispatch(setFiltersPayload({ sectionName, data: payload[sectionName] }));
    }
    const isTabletFlow = theme.isLargeScreen || theme.isTablet;
    const resolvedSectionName =
      isTabletFlow &&
      productType === ProductCategory.JEWELRY &&
      sectionName !== SectionName.JEWELRY &&
      sectionName !== SectionName.LABGROWN_JEWELRY
        ? SectionName.JEWELRY
        : sectionName;
    const tabletOnClick = () => {
      dispatch(setFiltersSet());
      dispatch(setSectionName(resolvedSectionName));
    };
    if (iframePayloadRef.current) {
      const iframePayload = iframePayloadRef.current;
      const selectedColor = iframePayload?.color;

      // Clone existing section payload
      const cleanedSectionPayload = { ...payload[sectionName] };

      // 🔥 CLEAR previous color selections
      delete cleanedSectionPayload.fancy_colors;
      delete cleanedSectionPayload.fancyColors1;
      delete cleanedSectionPayload.simpleColors;
      delete cleanedSectionPayload.color_from;
      delete cleanedSectionPayload.color_to;

      // Apply current color
      if (selectedColor === "black") {
        cleanedSectionPayload.fancy_colors = ["black"];
        cleanedSectionPayload.fancyColors1 = ["black"];
      } else if (selectedColor === "white") {
        cleanedSectionPayload.color_from = "D";
        cleanedSectionPayload.color_to = "Z";
        cleanedSectionPayload.simpleColors = ["D", "Z"];
      }

      payload[sectionName] = {
        ...cleanedSectionPayload,

        // MM
        meas_length_from: iframePayload.meas_length_from,
        meas_length_to: iframePayload.meas_length_to,

        // CARAT
        size_from: iframePayload.size_from,
        size_to: iframePayload.size_to,

        // SHAPE
        shapes: iframePayload.shapes,
      };
    }
    const updatedAuxPayload = handleFilters(sectionName, payload);
    dispatch(setPayloadData({ data: { ...updatedAuxPayload }, productType }));
    (theme.isLargeScreen || theme.isTablet) && tabletOnClick();
    const name =
      theme.isLargeScreen || theme.isTablet
        ? Routes.FilterSplitScreen
        : Routes.SearchResultScreen;
    const appliedCollection =
      Array.isArray(collections) && collections.length > 0
        ? collections.join(",")
        : selectedCollectionOption?.collection;
    const savedSearch = {
      ...updatedAuxPayload[sectionName],
    };
    delete savedSearch.collection_names;
    delete savedSearch.sub_collection;
    const isLabGrownJewelrySearch =
      resolvedSectionName === SectionName.LABGROWN_JEWELRY ||
      productType === ProductCategory.LABGROWN_JEWELRY;
    if (isLabGrownJewelrySearch) {
      savedSearch.sub_type = ProductTypeCommon.LABGROWN_JEWELRY_SUB_TYPE;
    } else if (
      savedSearch.sub_type === ProductTypeCommon.LABGROWN_JEWELRY_SUB_TYPE
    ) {
      delete savedSearch.sub_type;
    }
    const params = {
      priceMode,
      productType: productType,
      fromNewFilterScreen: fromResultScreen,
      filterSplitStep: fromResultScreen ? 2 : 1,
      sectionName: resolvedSectionName,
      productPrefix,
      breadCrumbLabel:
        productType === ProductCategory.JEWELRY ||
        productType === ProductCategory.LABGROWN_JEWELRY
          ? "Setting Search"
          : "Stone Search",
      enterSecondFlow: normalizedEnterSecondFlow,
      saved_search: JSON.stringify(savedSearch),
      ...(appliedCollection ? { collection: appliedCollection } : {}),
    };
    const route = { name, params };
    if (isPDPLoaded) {
      goBack();
    } else {
      if (enableJewelryBuilder) {
        if (!fromResultScreen) {
          if (history.length === 1) {
            const mRoutes = history.map((scr) => ({
              name: scr.screen,
              params: scr.params,
            }));

            const breadCrumb = mRoutes[0].params?.breadCrumbLabel;

            if (
              breadCrumb === "Setting Search" ||
              breadCrumb === "Stone Search"
            ) {
              clearRouteHistory();
            }
          }
          addRouteToHistory(route);
          dispatch(setBuilderProduct(productType));
        }
        if (isOnSplitScreen || normalizedEnterSecondFlow === true) {
          navigate(name, params);
        } else {
          push(name, params);
        }
      } else {
        navigate(name, params);
      }
    }
  };

  const onRemoveFilters = () => {
    if (showDiamondClassicAiTabs) {
      removeSecureStoreItems(["vdbchat_conversations"]);
    }
    const parentCollectionOption = selectedCollectionOption;
    if (
      productType === ProductCategory.JEWELRY ||
      productType === ProductCategory.LABGROWN_JEWELRY
    ) {
      DeviceEventEmitter.emit("RESET_FILTERS_EVENT");
      dispatch(
        resetJewelryFiltersPayload({
          setPreservedData,
          productType,
        })
      );
    } else {
      DeviceEventEmitter.emit("RESET_FILTERS_EVENT");
      dispatch(resetFiltersPayloadByProduct(productType));
    }
    dispatch(clearSubCollections());

    // Keep the parent collection (e.g. Bridals) after Reset All.
    if (parentCollectionOption?.collection) {
      dispatch(
        setSelectedCollectionOption({
          name: parentCollectionOption.name,
          label: parentCollectionOption.label,
          collection: parentCollectionOption.collection,
          sectionName: parentCollectionOption.sectionName,
          predefined_payload: parentCollectionOption.predefined_payload,
        })
      );
      dispatch(setCollection(parentCollectionOption.collection));
      const collectionSectionName =
        parentCollectionOption.sectionName || sectionName;
      if (parentCollectionOption.predefined_payload && collectionSectionName) {
        dispatch(
          setFiltersPayload({
            sectionName: collectionSectionName,
            data: parentCollectionOption.predefined_payload,
          })
        );
        dispatch(
          setPayloadData({
            productType,
            data: {
              [collectionSectionName]:
                parentCollectionOption.predefined_payload,
            },
          })
        );
      }
    }
  };
  const ResetButton = () => {
    if (enableJewelryBuilder ? normalizedEnterSecondFlow === false : true) {
      return (
        <Button
          testID="filter.reset-button"
          category={productTheme as ButtonCategory}
          type={ButtonType.tertiary}
          title={t("searchResult.web.screenOptions.resetAll.text")}
          onPress={onRemoveFilters}
          maxWidth={
            theme.isLargeScreen ? (fromResultScreen ? 148 : 164) : undefined
          }
          width={buttonWidth}
        />
      );
    } else {
      return;
    }
  };

  return (
    <Container>
      <MarginContainer
        scrollBarWidth={useScrollbarWidth()}
        fromResultScreen={fromResultScreen}
      >
        <Section
          paddingBottom={
            isIFrameFlow() && Platform.OS === "web" && theme.isMobile
              ? 0
              : bottom
          }
          showAsTopButtons={showAsTopButtons}
          fromResultScreen={fromResultScreen}
          containerBackgroundColor={containerBackgroundColor}
          isIFrame={isIFrameFlow}
        >
          <ResetButton />
          <Button
            testID="filter.apply-button"
            category={productTheme as ButtonCategory}
            type={ButtonType.primary}
            title={
              fromResultScreen
                ? t("searchResult.web.screenOptions.apply.text")
                : showSearchWithArrow
                ? t("searchResult.web.screenOptions.search.text")
                : t("searchResult.web.screenOptions.apply.text")
            }
            showIcon={!fromResultScreen && showSearchWithArrow}
            iconSource={VDBIcons.RIGHT}
            iconSize={24}
            onPress={onApplyFilters}
            width={buttonWidth}
            {...(((enableJewelryBuilder &&
              normalizedEnterSecondFlow === false) ||
              !enableJewelryBuilder) && {
              maxWidth: theme.isLargeScreen
                ? fromResultScreen
                  ? 148
                  : 164
                : undefined,
            })}
            iconColor={theme["primary-cont"]}
            textTypographyStyle={"H3-M"}
          />
        </Section>
      </MarginContainer>
    </Container>
  );
};

const Section = styled.View<{
  paddingBottom: number;
  showAsTopButtons?: boolean;
  fromResultScreen?: boolean;
  containerBackgroundColor: string;
  isIFrame?: boolean;
  scrollBarWidth?: number;
}>`
  flex-direction: row;
  justify-content: ${({
    theme,
    fromResultScreen,
    isIFrame,
  }: {
    theme: DefaultTheme;
    fromResultScreen: boolean;
    isIFrame: any;
  }) =>
    isIFrame() && !fromResultScreen && !theme.isMobile
      ? "flex-end"
      : (theme.isTablet || theme.isLargeScreen) && !fromResultScreen
      ? "space-between"
      : "center"};
  background: ${({
    theme,
    containerBackgroundColor,
  }: {
    theme: DefaultTheme;
    containerBackgroundColor: string;
  }) =>
    containerBackgroundColor
      ? theme[containerBackgroundColor as keyof DefaultTheme]
      : theme["core-01"]};
  width: ${({
    theme,
    fromResultScreen,
  }: {
    theme: DefaultTheme;
    fromResultScreen: boolean;
  }) => (theme.isLargeScreen && !fromResultScreen ? "80%" : "100%")};
  gap: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? SpaceType.Basic : SpaceType.Double}px;
  padding: ${({
    theme,
    showAsTopButtons,
    fromResultScreen,
  }: {
    theme: DefaultTheme;
    showAsTopButtons: boolean;
    fromResultScreen: boolean;
  }) =>
    theme.isTablet && !fromResultScreen
      ? "0px"
      : showAsTopButtons
      ? "0px"
      : `${SpaceType.Double}px 0px`};
  height: ${({
    theme,
    showAsTopButtons,
    fromResultScreen,
    isIFrame,
  }: {
    theme: DefaultTheme;
    showAsTopButtons: boolean;
    fromResultScreen: boolean;
    isIFrame: any;
  }) =>
    showAsTopButtons
      ? "42px"
      : theme.isLargeScreen
      ? "70px"
      : fromResultScreen && theme.isTablet && !isIFrame()
      ? "150px"
      : isIFrame() && theme.isTablet
      ? "70px"
      : isIFrame() && theme.isMobile
      ? "auto"
      : "100px"};
  padding-bottom: ${({
    paddingBottom,
    theme,
    showAsTopButtons,
    fromResultScreen,
  }: {
    paddingBottom: number;
    theme: DefaultTheme;
    showAsTopButtons: boolean;
    fromResultScreen: boolean;
  }) =>
    theme.isTablet && !fromResultScreen
      ? 0
      : showAsTopButtons
      ? 0
      : (theme.isLargeScreen || theme.isTablet) && Platform.OS === "android"
      ? SpaceType.large
      : Platform.OS === "android" && theme.isMobile
      ? paddingBottom + 5
      : paddingBottom}px;
  ${({
    theme,
    fromResultScreen,
  }: {
    theme: DefaultTheme;
    fromResultScreen: boolean;
  }) =>
    theme.isTablet &&
    !fromResultScreen &&
    css`
      padding-left: ${SpaceType.Big}px;
      padding-right: ${SpaceType.Big}px;
      align-items: center;
    `}
  ${({
    theme,
    fromResultScreen,
  }: {
    theme: DefaultTheme;
    fromResultScreen: boolean;
    scrollBarWidth: number;
  }) =>
    theme.isLargeScreen &&
    !fromResultScreen &&
    css`
      max-width: 1432px;
      align-self: center;
    `};
  ${({
    theme,
    fromResultScreen,
  }: {
    theme: DefaultTheme;
    fromResultScreen: boolean;
    scrollBarWidth: number;
  }) =>
    theme.isMobile &&
    !fromResultScreen &&
    css`
      padding-horizontal: ${SpaceType.Double}px;
      padding-top: ${SpaceType.Basic}px;
    `}
`;

const Container = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
`;

const MarginContainer = styled.View<{
  scrollBarWidth: number;
  fromResultScreen: boolean;
}>`
  margin-right: ${({
    scrollBarWidth,
    fromResultScreen,
  }: {
    scrollBarWidth: number;
    fromResultScreen: boolean;
  }) => (fromResultScreen ? 0 : scrollBarWidth)}px;
  ${({ theme }) =>
    theme.isLargeScreen &&
    css`
      padding-horizontal: ${SpaceType.Double}px;
    `}
  border-top-width:1px;
  border-top-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

export default ButtonSection;
